import { PREDEFINED_MESSAGES_MODES } from 'constants/InterventionConstants';
import { DEFAULT_LANGUAGE } from 'constants/LanguageConstants';

/**
 * @param {{[language: string]: string}} translation
 * @returns {string}
 */
const getTranslationFallback = translation => translation?.[DEFAULT_LANGUAGE] ?? Object.values(translation ?? {}).shift();

/**
 * @param {string} language
 * @returns {({[language: string]: string}, {[language: string]: string}) => number}
 */
const sortTranslations = language => (a, b) => {
  const aTitle = a.title[language];
  const bTitle = b.title[language];
  return aTitle.localeCompare(bTitle);
};

/**
 * @param {Array<import("./types").PredefinedMessage>} predefinedMessages
 * @param {string} language
 * @param {string} dealerLanguage
 * @returns {Array<import("./types").PredefinedMessage>}
 */
export const sortPredefinedMessages = (predefinedMessages, language, dealerLanguage) => {
  const messages = predefinedMessages.filter(({ id }) => id !== PREDEFINED_MESSAGES_MODES.DEFAULT);
  const [defaultPredefinedMessage] = predefinedMessages.filter(({ id }) => id === PREDEFINED_MESSAGES_MODES.DEFAULT);

  const languageMessages = messages
    .filter(predefinedMessage => predefinedMessage.title[language])
    .sort(sortTranslations(language));

  const withoutLanguageMessages = messages.filter(predefinedMessage => !predefinedMessage.title[language]);

  const dealerLanguageMessages = withoutLanguageMessages
    .filter(predefinedMessage => predefinedMessage.title[dealerLanguage])
    .sort(sortTranslations(dealerLanguage));

  const withoutDealerLanguageMessages = withoutLanguageMessages
    .filter(predefinedMessage => !predefinedMessage.title[dealerLanguage])
    .sort((a, b) => {
      const aTitle = getTranslationFallback(a.title);
      const bTitle = getTranslationFallback(b.title);
      return aTitle.localeCompare(bTitle);
    });

  return [
    ...(defaultPredefinedMessage ? [defaultPredefinedMessage] : []),
    ...languageMessages,
    ...dealerLanguageMessages,
    ...withoutDealerLanguageMessages,
  ];
};

/**
 * Gets a predefined message id from a content and a language, returns `'none'` by default.
 * @param {string} content
 * @param {string} language
 * @param {Array<import("./types").PredefinedMessage>} predefinedMessages
 * @returns {string} predefined message id
 */
export const getPredefinedMessageIdByContent = (content, language, predefinedMessages) => {
  let predefinedMessage;

  if (predefinedMessages?.length > 0) {
    predefinedMessage = predefinedMessages.find(message => message.content?.[language] === content)?.id;
    if (!predefinedMessage) {
      predefinedMessage = predefinedMessages.find(({ content: predefinedContent }) =>
        Object.values(predefinedContent).find(value => value === content))?.id;
    }
  }

  return predefinedMessage ?? PREDEFINED_MESSAGES_MODES.NONE;
};

/**
 * @param {import('modules/global/typing/common/Label').Label} data
 * @param {string} titleKey
 * @param {string} contentKey
 * @returns {import('./types').PredefinedMessage}
 */
export const computeDefaultPredefinedMessage = (data, titleKey, contentKey) => {
  const languages = Object.keys(data);
  return {
    id: PREDEFINED_MESSAGES_MODES.DEFAULT,
    title: languages.reduce((acc, language) => ({ ...acc, [language]: data[language][titleKey] }), {}),
    content: languages.reduce((acc, language) => ({ ...acc, [language]: data[language][contentKey] ?? data[language][titleKey] }), {}),
  };
};
